import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { LucideAngularModule, Phone, Video, VideoOff } from 'lucide-angular';
import { CallKind } from '../../../core/models/call.model';
import { PhotoAppelComponent } from './photo-appel.component';
import { TranslationService } from '../../../core/services/translation.service';

/**
 * Ecran « appel entrant » — maquette « Appels — palette claire », ecrans 1 (audio) et 4 (video).
 *
 * Presentationnel : il ne sait ni repondre ni refuser, il le signale. La sonnerie et la
 * notification restent a `CallOverlayComponent`, qui les pilote selon la phase.
 *
 * DEUX THEMES : clair par defaut (la maquette), sombre quand `html.dark` — les anciens ecrans d'appel,
 * dont la disposition est la meme.
 *
 * ABSENT DE LA MAQUETTE, VOLONTAIREMENT :
 *  - la ligne « Caisse principale · appelle depuis un POS » : aucune donnee ne dit d'ou
 *    appelle l'appelant, l'inventer serait mentir ;
 *
 * « Sans video » n'apparait que pour un appel video : repondre sans allumer la camera.
 */
@Component({
    selector: 'app-appel-entrant',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LucideAngularModule, PhotoAppelComponent],
    template: `
    <div class="fixed inset-0 z-50 overflow-hidden text-surface-900 dark:text-[#f4f4fe] bg-gradient-to-b from-white to-surface-50
                dark:bg-[radial-gradient(130%_95%_at_15%_-10%,#332a86_0%,#1c1550_34%,#0c0930_62%,#020103_100%)]"
         role="dialog" aria-modal="true" [attr.aria-label]="titre()">
      <div class="lueur-haute absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>
      <div class="lueur-laterale absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>

      <div class="relative z-[1] h-full flex flex-col">
        <div class="flex-none pt-7 px-6 text-center text-xs font-semibold uppercase tracking-[0.08em] text-surface-500 dark:text-[#d8d6f7]">{{ titre() }}</div>

        <div class="flex-1 flex flex-col items-center justify-center p-6">
          <div class="avatar-sonnerie w-32 h-32 rounded-full overflow-hidden mb-6 flex items-center justify-center text-[40px] font-bold
                      bg-surface-100 border border-surface-200 text-surface-700
                      dark:border-0 dark:bg-[linear-gradient(150deg,#c7d2fe_0%,#818cf8_55%,#4338ca_100%)] dark:text-[#1e1b4b]"><app-photo-appel [photo]="photo()" [nom]="nom()" [initiales]="initiales()" /></div>
          <div class="text-2xl font-bold text-center">{{ nom() }}</div>
          <div class="mt-1.5 flex items-center gap-1.5 text-sm leading-[normal] text-surface-500 dark:text-[#a5a1d6]">
            @if (estVideo()) {
              <lucide-icon [img]="Video" [size]="15" aria-hidden="true"></lucide-icon>{{ t().calls.type_video }}…
            } @else {
              <lucide-icon [img]="Phone" [size]="14" aria-hidden="true"></lucide-icon>{{ t().calls.type_audio }}…
            }
          </div>
        </div>

        <div class="flex-none pb-14 items-start" [class]="estVideo() ? 'px-10 grid grid-cols-3 gap-2' : 'px-14 flex justify-between'">
          <div class="flex flex-col items-center gap-2.5">
            <button type="button" (click)="refuser.emit()" class="bouton w-16 h-16 rounded-full flex items-center justify-center border
                    bg-danger-50 border-danger-200 text-danger-600
                    dark:border-0 dark:text-white dark:bg-[linear-gradient(160deg,#fb7185,#e11d48_65%,#be123c)] dark:shadow-[0_16px_30px_-10px_rgba(225,29,72,0.65)]"
                    [attr.aria-label]="t().calls.decline">
              <span class="inline-flex rotate-[135deg]" aria-hidden="true"><lucide-icon [img]="Phone" [size]="26"></lucide-icon></span>
            </button>
            <span class="text-[13px] leading-[normal] font-semibold text-surface-600 dark:text-[rgba(244,244,254,0.75)]">{{ t().calls.decline }}</span>
          </div>

          @if (estVideo()) {
            <div class="flex flex-col items-center gap-2.5">
              <button type="button" (click)="repondreAudioSeul.emit()" class="bouton w-[52px] h-[52px] mt-1.5 rounded-full flex items-center justify-center border
                      bg-white border-surface-200 text-surface-700
                      dark:bg-[#2b2760] dark:border-[rgba(255,255,255,0.18)] dark:text-[#f4f4fe]"
                      [attr.aria-label]="t().calls.audio_only_aria">
                <lucide-icon [img]="VideoOff" [size]="20" aria-hidden="true"></lucide-icon>
              </button>
              <span class="text-xs leading-[normal] font-semibold text-center text-surface-600 dark:text-[rgba(244,244,254,0.6)]">{{ t().calls.audio_only }}</span>
            </div>
          }

          <div class="flex flex-col items-center gap-2.5">
            <button type="button" (click)="repondre.emit()" class="bouton w-16 h-16 rounded-full flex items-center justify-center border
                    bg-success-50 border-success-200 text-success-600
                    dark:border-0 dark:text-white dark:bg-[linear-gradient(160deg,#4ade80,#16a34a_65%,#15803d)] dark:shadow-[0_16px_30px_-10px_rgba(22,163,74,0.6)]"
                    [attr.aria-label]="t().calls.answer">
              <lucide-icon [img]="estVideo() ? Video : Phone" [size]="26" aria-hidden="true"></lucide-icon>
            </button>
            <span class="text-[13px] leading-[normal] font-semibold text-surface-600 dark:text-[rgba(244,244,254,0.75)]">{{ t().calls.answer }}</span>
          </div>
        </div>
      </div>
    </div>
  `,
    styles: [
        `
      .lueur-haute { top:-130px; right:-100px; width:360px; height:360px; border-radius:9999px;
        background:radial-gradient(circle,rgba(129,140,248,0.34) 0%,rgba(129,140,248,0) 70%); filter:blur(4px);
        animation:derive-1 16s ease-in-out infinite; }
      .lueur-laterale { top:220px; left:-120px; width:300px; height:300px; border-radius:9999px;
        background:radial-gradient(circle,rgba(56,189,248,0.16) 0%,rgba(56,189,248,0) 70%); filter:blur(6px);
        animation:derive-2 13s ease-in-out infinite; }
      .avatar-sonnerie { animation:anneau-clair 2s ease-out infinite; }
      :host-context(.dark) .avatar-sonnerie { animation:pulse-sonnerie 2.2s ease-in-out infinite; }
      .bouton { transition:transform .15s ease; }
      .bouton:active { transform:scale(0.92); }
      @keyframes anneau-clair { 0%,100% { box-shadow:0 0 0 0 rgba(13,148,136,0.16); } 50% { box-shadow:0 0 0 20px rgba(13,148,136,0); } }
      @keyframes pulse-sonnerie {
        0%,100% { box-shadow:0 0 0 8px rgba(129,140,248,0.16),0 20px 36px -10px rgba(67,56,202,0.6); }
        50% { box-shadow:0 0 0 18px rgba(129,140,248,0),0 20px 36px -10px rgba(67,56,202,0.6); }
      }
      @keyframes derive-1 { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(-18px,14px) scale(1.06); } }
      @keyframes derive-2 { 0%,100% { transform:translate(0,0); } 50% { transform:translate(16px,-10px); } }
      @media (prefers-reduced-motion: reduce) {
        .lueur-haute, .lueur-laterale, .avatar-sonnerie { animation:none !important; }
      }
    `,
    ],
})
export class AppelEntrantComponent {
    readonly nom = input.required<string>();
    readonly type = input.required<CallKind>();
    /** Photo de profil de la personne qui appelle ; ses initiales a defaut. */
    readonly photo = input<string | null | undefined>();
    readonly repondre = output<void>();
    /** Repondre a un appel video sans allumer la camera. */
    readonly repondreAudioSeul = output<void>();
    readonly refuser = output<void>();

    readonly t = inject(TranslationService).t;

    readonly Phone = Phone;
    readonly Video = Video;
    readonly VideoOff = VideoOff;

    readonly estVideo = computed(() => this.type() === 'video');
    readonly titre = computed(() => (this.estVideo() ? this.t().calls.incoming_video : this.t().calls.incoming_audio));

    /** Initiales de l'appelant, pour l'avatar : jamais de photo inventee. */
    readonly initiales = computed(() =>
        this.nom().split(/\s+/).filter(Boolean).slice(0, 2).map((m) => m.charAt(0).toUpperCase()).join(''),
    );
}
